import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import type { Locale } from '~/stores/app/types';

import type { CmsPage, Footer, Menu, Reassurance } from './types';

const STORE_ID = 'cms';

interface Actions {
  fetchCmsPage: (slug: string, locale: Locale) => Promise<CmsPage | null>;
  fetchFooter: (locale: Locale) => Promise<Footer | null>;
  fetchMenu: (locale: Locale) => Promise<Menu | null>;
  fetchReassurance: (locale: Locale) => Promise<Reassurance | null>;
}

interface State {
  cmsPage: CmsPage | null;
  footer: Footer | null;
  menu: Menu | null;
  reassurance: Reassurance | null;
}

export const useCmsStore = defineStore<typeof STORE_ID, State, _GettersTree<State>, Actions>(STORE_ID, {
  actions: {
    async fetchCmsPage(slug: string, locale: Locale) {
      try {
        const response = await this.$api.fetch<CmsPage>(ApiMethod.GET, `/cms/page/${slug}`, {
          locale,
        });

        if (response.data) {
          this.cmsPage = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        console.error('An error occurred while getting CMS page', getErrorMessage(fetchError));

        return null;
      }

      return null;
    },
    async fetchFooter(locale: Locale) {
      try {
        const response = await this.$api.fetch<Footer>(ApiMethod.GET, '/cms/footer', {
          locale,
        });

        if (response.data) {
          this.footer = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        console.error('An error occurred while getting footer', getErrorMessage(fetchError));

        return null;
      }

      return null;
    },
    async fetchMenu(locale: Locale) {
      try {
        const response = await this.$api.fetch<Menu>(ApiMethod.GET, '/cms/menu', {
          locale,
        });

        if (response.data) {
          this.menu = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        console.error('An error occurred while getting menu cms data', getErrorMessage(fetchError));

        return null;
      }

      return null;
    },
    async fetchReassurance(locale: Locale) {
      try {
        const response = await this.$api.fetch<Reassurance>(ApiMethod.GET, '/cms/reassurance', {
          locale,
        });

        if (response.data) {
          this.reassurance = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        console.error('An error occurred while getting reassurance footer cms data', getErrorMessage(fetchError));

        return null;
      }

      return null;
    },
  },
  state: () => ({
    cmsPage: null,
    footer: null,
    menu: null,
    reassurance: null,
  }),
});
